import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import moment from 'moment';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

import { CouponStatusId } from '../../../../../types/enums/giftVoucher-enums';

import {
    Paper,
    Table,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
} from '@mui/material';

import { translationKeys, fieldNames, statusOptions, voucherTypes, IGiftVoucherData } from '../enum';
import { GiftVoucherQuery } from '../../../../../services/query';
import { GV } from '../giftVouchers.classes';

export default function GiftVoucherGrid({
    gridData,
    setPage,
    setRowsPerPage,
    setIsDrawerOpen,
    setIsLoading,
    voucherType,
    reset,
    setStartDate,
    setEndDate,
    getGiftVouchers,
    setSelectedGift,
    setVoucherType,
    totalCount,
    page,
    rowsPerPage,
}: any) {
    const { t } = useTranslation();

    const handleChangePage = (_: any, newPage: any) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: any) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    const getCellValue = (cellKey: any, cellValue: any) => {
        if (['startDate', 'endDate'].includes(cellKey)) {
            return moment(cellValue).format('DD/MM/YYYY');
        }
        if (cellKey === 'status') {
            return t(statusOptions.find((option: any) => option.id === cellValue)?.name || '');
        }
        if (cellKey === 'couponType') {
            return t(`giftVoucher.${voucherTypes.find((type: any) => type.id === cellValue)?.key}` || '');
        }
        if (cellKey === 'description') {
            return (
                <Tooltip title={cellValue}>
                    {cellValue?.length > 10 ? `${cellValue.slice(0, 10)}...` : cellValue}
                </Tooltip>
            );
        }
        if (['minOrderGrandTotalAmount', 'maxDiscountAmount'].includes(cellKey)) {
            return cellValue || 'N/A';
        }
        return cellValue;
    };

    const deleteGiftVoucher = (giftId: any) => {
        Swal.fire({
            text: t('general.sureText'),
            cancelButtonText: t('general.cancel'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('general.yes'),
        }).then((result: any) => {
            if (result.isConfirmed) {
                setIsLoading(true);
                GiftVoucherQuery.DeleteGiftVoucher({
                    id: giftId,
                    status: CouponStatusId.Deleted,
                })
                    .then(() => {
                        getGiftVouchers();
                    })
                    .catch(() => {
                        setIsLoading(false);
                        Swal.fire(t('general.error'), '', 'error');
                    });
            }
        });
    };

    const getGiftVoucherById = (payloadId: any) => {
        setIsLoading(true);
        GiftVoucherQuery.GetGiftVoucherById({
            Id: payloadId,
        })
            .then((res: any) => {
                const resetData: IGiftVoucherData = {
                    id: res.data?.giftVoucher?.id || undefined,
                    status: res.data?.giftVoucher?.status || null,
                    value: res.data?.giftVoucher?.value || 0,
                    minAmount: res.data?.giftVoucher?.minOrderGrandTotalAmount || 0,
                    maxAmount:
                        res.data?.giftVoucher?.maxDiscountAmount ||
                        voucherTypes.find((item: any) => item.id === voucherType)?.key === 'amount'
                            ? 999999
                            : 0,
                    description: res.data?.giftVoucher?.description || '',
                    couponSource: res.data?.giftVoucher?.couponSource || null,
                };
                (res.data?.titleTexts || []).forEach((langItem: any) => {
                    resetData[`language-${langItem.languageId || ''}`] = langItem.keywordValue || '';
                });

                reset(resetData);
                setVoucherType(res.data?.giftVoucher?.couponType);
                setStartDate(moment(res.data?.giftVoucher?.startDate).format('DD/MM/YYYY') || '');
                setEndDate(moment(res.data?.giftVoucher?.endDate).format('DD/MM/YYYY') || '');
                setSelectedGift(res.data);
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
                Swal.fire(t('general.error'), '', 'error');
            });
    };

    return (
        <Grid
            className={GV.gridSection}
            sx={{ overflow: 'scroll' }}
            size={{
                sm: 12,
                md: 12,
                lg: 12,
                xl: 12,
            }}
        >
            <Table component={Paper} className={GV.table}>
                <TableHead>
                    <TableRow>
                        {translationKeys.map((headerKey: any, index: number) => (
                            <TableCell key={index} style={{ textAlign: 'center' }}>
                                {t(headerKey)}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                {(gridData || []).map((gift: any, key: number) => {
                    return (
                        <TableRow key={key}>
                            {fieldNames.map((field: any) => (
                                <TableCell key={field} style={{ textAlign: 'center' }}>
                                    {getCellValue(field, gift[field])}
                                </TableCell>
                            ))}
                            <TableCell style={{ textAlign: 'center' }}>
                                <PopupState variant="popover" popupId="demo-popup-menu">
                                    {(popupState: any) => (
                                        <>
                                            <IconButton {...bindTrigger(popupState)} className={GV.inputIconButtonRowMenu}>
                                                <MoreVertIcon className="svg_icons" />
                                            </IconButton>
                                            <Menu {...bindMenu(popupState)}>
                                                <MenuItem
                                                    className={GV.inputMenuItemEdit}
                                                    disabled={gift.status === CouponStatusId.Exhausted}
                                                    onClick={() => {
                                                        setIsDrawerOpen(true);
                                                        getGiftVoucherById(gift.id);
                                                        popupState.close();
                                                    }}
                                                >
                                                    {t('general.edit')}
                                                </MenuItem>
                                                <MenuItem
                                                    className={GV.inputMenuItemDelete}
                                                    disabled={gift.status === CouponStatusId.Exhausted}
                                                    onClick={async () => {
                                                        deleteGiftVoucher(gift.id);
                                                        popupState.close();
                                                    }}
                                                >
                                                    {t('general.delete')}
                                                </MenuItem>
                                            </Menu>
                                        </>
                                    )}
                                </PopupState>
                            </TableCell>
                        </TableRow>
                    );
                })}
            </Table>
            <TablePagination
                className={GV.tablePagination}
                rowsPerPageOptions={[5, 10, 20, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Grid>
    );
}
